/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  Input,
  Optional,
  Output,
  QueryList,
  ChangeDetectionStrategy,
  EventEmitter,
  Renderer2,
  ViewEncapsulation,
  NgZone,
  OnDestroy, Inject,
} from '@angular/core';
import {Dir, coerceBooleanProperty} from '../core';
import {FocusTrapFactory, FocusTrap} from '../core/a11y/focus-trap';
import {ESCAPE} from '../core/keyboard/keycodes';
import'rxjs/add/operator/first';
import {DOCUMENT} from '@angular/platform-browser';
/**
 * Throws an exception when two MdSidenav are matching the same side.
 * @param {?} align
 * @return {?}
 */
export function throwMdDuplicatedSidenavError(align: string) {
  throw Error(`A sidenav was already declared for 'align="${align}"'`);
}
/**
 * Sidenav toggle promise result.
 */
export class MdSidenavToggleResult {
/**
 * @param {?} type
 * @param {?} animationFinished
 */
constructor(public type: 'open' | 'close',
public animationFinished: boolean) {}
}

function MdSidenavToggleResult_tsickle_Closure_declarations() {
/** @type {?} */
MdSidenavToggleResult.prototype.type;
/** @type {?} */
MdSidenavToggleResult.prototype.animationFinished;
}

/**
 * <md-sidenav> component.
 * 
 * This component corresponds to the drawer of the sidenav.
 * 
 * Please refer to README.md for examples on how to use it.
 */
export class MdSidenav implements AfterContentInit, OnDestroy {
private _focusTrap: FocusTrap;
/**
 * Alignment of the sidenav (direction neutral); whether 'start' or 'end'.
 */
private _align: 'start' | 'end' = 'start';
/**
 * Direction which the sidenav is aligned in.
 * @return {?}
 */
get align() { return this._align; }
/**
 * @param {?} value
 * @return {?}
 */
set align(value) {
    // Make sure we have a valid value.
    value = (value == 'end') ? 'end' : 'start';
    if (value != this._align) {
      this._align = value;
      this.onAlignChanged.emit();
    }
  }
/**
 * Mode of the sidenav; one of 'over', 'push' or 'side'.
 */
mode: 'over' | 'push' | 'side' = 'over';
/**
 * Whether the sidenav can be closed with the escape key or not.
 * @return {?}
 */
get disableClose(): boolean { return this._disableClose; }
/**
 * @param {?} value
 * @return {?}
 */
set disableClose(value: boolean) { this._disableClose = coerceBooleanProperty(value); }
private _disableClose: boolean = false;
/**
 * Whether the sidenav is opened.
 */
_opened: boolean = false;
/**
 * Event emitted when the sidenav is being opened. Use this to synchronize animations.
 */
onOpenStart = new EventEmitter<void>();
/**
 * Event emitted when the sidenav is fully opened.
 */
onOpen = new EventEmitter<void>();
/**
 * Event emitted when the sidenav is being closed. Use this to synchronize animations.
 */
onCloseStart = new EventEmitter<void>();
/**
 * Event emitted when the sidenav is fully closed.
 */
onClose = new EventEmitter<void>();
/**
 * Event emitted when the sidenav alignment changes.
 */
onAlignChanged = new EventEmitter<void>();
/**
 * The current toggle animation promise. `null` if no animation is in progress.
 */
private _toggleAnimationPromise: Promise<MdSidenavToggleResult> = null;
/**
 * The current toggle animation promise resolution function.
 * `null` if no animation is in progress.
 */
private _resolveToggleAnimationPromise: (animationFinished: boolean) => void = null;
/**
 * @return {?}
 */
get isFocusTrapEnabled() {
    // The focus trap is only enabled when the sidenav is open in any mode other than side.
    return this.opened && this.mode !== 'side';
  }
/**
 * @param {?} _elementRef The DOM element reference. Used for transition and width calculation.
 *     If not available we do not hook on transitions.
 * @param {?} _focusTrapFactory
 * @param {?} _doc
 */
constructor(private _elementRef: ElementRef,
private _focusTrapFactory: FocusTrapFactory,
private _doc: any) {
    this.onOpen.subscribe(() => {
      if (this._doc) {
        this._elementFocusedBeforeSidenavWasOpened = this._doc.activeElement as HTMLElement;
      }

      if (this.isFocusTrapEnabled && this._focusTrap) {
        this._focusTrap.focusInitialElementWhenReady();
      }
    });

    this.onClose.subscribe(() => this._restoreFocus());
  }
/**
 * If focus is currently inside the sidenav, restores it to where it was before the sidenav
 * opened.
 * @return {?}
 */
private _restoreFocus() {
    let /** @type {?} */ activeEl = this._doc && this._doc.activeElement;
    if (activeEl && this._elementRef.nativeElement.contains(activeEl)) {
      if (this._elementFocusedBeforeSidenavWasOpened instanceof HTMLElement) {
        this._elementFocusedBeforeSidenavWasOpened.focus();
      } else {
        this._elementRef.nativeElement.blur();
      }
    }

    this._elementFocusedBeforeSidenavWasOpened = null;
  }
/**
 * @return {?}
 */
ngAfterContentInit() {
    this._focusTrap = this._focusTrapFactory.create(this._elementRef.nativeElement);
    this._focusTrap.enabled = this.isFocusTrapEnabled;

    // This can happen when the sidenav is set to opened in
    // the template and the transition hasn't ended.
    if (this._toggleAnimationPromise) {
      this._resolveToggleAnimationPromise(true);
      this._toggleAnimationPromise = this._resolveToggleAnimationPromise = null;
    }
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    if (this._focusTrap) {
      this._focusTrap.destroy();
    }
  }
/**
 * Whether the sidenav is opened. We overload this because we trigger an event when it
 * starts or end.
 * @return {?}
 */
get opened(): boolean { return this._opened; }
/**
 * @param {?} v
 * @return {?}
 */
set opened(v: boolean) {
    this.toggle(coerceBooleanProperty(v));
  }
/**
 * Open this sidenav, and return a Promise that will resolve when it's fully opened (or get
 * rejected if it didn't).
 * @return {?}
 */
open(): Promise<MdSidenavToggleResult> {
    return this.toggle(true);
  }
/**
 * Close this sidenav, and return a Promise that will resolve when it's fully closed (or get
 * rejected if it didn't).
 * @return {?}
 */
close(): Promise<MdSidenavToggleResult> {
    return this.toggle(false);
  }
/**
 * Toggle this sidenav. This is equivalent to calling open() when it's already opened, or
 * close() when it's closed.
 * @param {?=} isOpen Whether the sidenav should be open.
 * @return {?} Resolves with the result of whether the sidenav was opened or closed.
 */
toggle(isOpen: boolean = !this.opened): Promise<MdSidenavToggleResult> {
    // Shortcut it if we're already opened.
    if (isOpen === this.opened) {
      return this._toggleAnimationPromise ||
          Promise.resolve(new MdSidenavToggleResult(isOpen ? 'open' : 'close', true));
    }

    this._opened = isOpen;

    if (this._focusTrap) {
      this._focusTrap.enabled = this.isFocusTrapEnabled;
    }

    if (isOpen) {
      this.onOpenStart.emit();
    } else {
      this.onCloseStart.emit();
    }

    if (this._toggleAnimationPromise) {
      this._resolveToggleAnimationPromise(false);
    }
    this._toggleAnimationPromise = new Promise<MdSidenavToggleResult>(resolve => {
      this._resolveToggleAnimationPromise = animationFinished =>
          resolve(new MdSidenavToggleResult(isOpen ? 'open' : 'close', animationFinished));
    });
    return this._toggleAnimationPromise;
  }
/**
 * Handles the keyboard events.
 * \@docs-private
 * @param {?} event
 * @return {?}
 */
handleKeydown(event: KeyboardEvent) {
    if (event.keyCode === ESCAPE && !this.disableClose) {
      this.close();
      event.stopPropagation();
    }
  }
/**
 * When transition has finished, set the internal state for classes and emit the proper event.
 * The event passed is actually of type TransitionEvent, but that type is not available in
 * Android so we use any.
 * @param {?} transitionEvent
 * @return {?}
 */
_onTransitionEnd(transitionEvent: TransitionEvent) {
    if (transitionEvent.target == this._elementRef.nativeElement
        // Simpler version to check for prefixes.
        && transitionEvent.propertyName.endsWith('transform')) {
      if (this._opened) {
        this.onOpen.emit();
      } else {
        this.onClose.emit();
      }

      if (this._toggleAnimationPromise) {
        this._resolveToggleAnimationPromise(true);
        this._toggleAnimationPromise = this._resolveToggleAnimationPromise = null;
      }
    }
  }
/**
 * @return {?}
 */
get _isClosing() {
    return !this._opened && !!this._toggleAnimationPromise;
  }
/**
 * @return {?}
 */
get _isOpening() {
    return this._opened && !!this._toggleAnimationPromise;
  }
/**
 * @return {?}
 */
get _isClosed() {
    return !this._opened && !this._toggleAnimationPromise;
  }
/**
 * @return {?}
 */
get _isOpened() {
    return this._opened && !this._toggleAnimationPromise;
  }
/**
 * @return {?}
 */
get _isEnd() {
    return this.align == 'end';
  }
/**
 * @return {?}
 */
get _modeSide() {
    return this.mode == 'side';
  }
/**
 * @return {?}
 */
get _modeOver() {
    return this.mode == 'over';
  }
/**
 * @return {?}
 */
get _modePush() {
    return this.mode == 'push';
  }
/**
 * @return {?}
 */
get _width() {
    if (this._elementRef.nativeElement) {
      return this._elementRef.nativeElement.offsetWidth;
    }
    return 0;
  }
private _elementFocusedBeforeSidenavWasOpened: HTMLElement = null;
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-sidenav, mat-sidenav',
  // TODO(mmalerba): move template to separate file.
  templateUrl: 'sidenav.html',
  host: {
    'class': 'mat-sidenav',
    '(transitionend)': '_onTransitionEnd($event)',
    '(keydown)': 'handleKeydown($event)',
    // must prevent the browser from aligning text based on value
    '[attr.align]': 'null',
    '[class.mat-sidenav-closed]': '_isClosed',
    '[class.mat-sidenav-closing]': '_isClosing',
    '[class.mat-sidenav-end]': '_isEnd',
    '[class.mat-sidenav-opened]': '_isOpened',
    '[class.mat-sidenav-opening]': '_isOpening',
    '[class.mat-sidenav-over]': '_modeOver',
    '[class.mat-sidenav-push]': '_modePush',
    '[class.mat-sidenav-side]': '_modeSide',
    'tabIndex': '-1'
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: FocusTrapFactory, },
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [DOCUMENT, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'align': [{ type: Input },],
'mode': [{ type: Input },],
'disableClose': [{ type: Input },],
'onOpenStart': [{ type: Output, args: ['open-start', ] },],
'onOpen': [{ type: Output, args: ['open', ] },],
'onCloseStart': [{ type: Output, args: ['close-start', ] },],
'onClose': [{ type: Output, args: ['close', ] },],
'onAlignChanged': [{ type: Output, args: ['align-changed', ] },],
'opened': [{ type: Input },],
};
}

function MdSidenav_tsickle_Closure_declarations() {
/** @type {?} */
MdSidenav.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdSidenav.ctorParameters;
/** @type {?} */
MdSidenav.propDecorators;
/** @type {?} */
MdSidenav.prototype._focusTrap;
/**
 * Alignment of the sidenav (direction neutral); whether 'start' or 'end'.
 * @type {?}
 */
MdSidenav.prototype._align;
/**
 * Mode of the sidenav; one of 'over', 'push' or 'side'.
 * @type {?}
 */
MdSidenav.prototype.mode;
/** @type {?} */
MdSidenav.prototype._disableClose;
/**
 * Whether the sidenav is opened.
 * @type {?}
 */
MdSidenav.prototype._opened;
/**
 * Event emitted when the sidenav is being opened. Use this to synchronize animations.
 * @type {?}
 */
MdSidenav.prototype.onOpenStart;
/**
 * Event emitted when the sidenav is fully opened.
 * @type {?}
 */
MdSidenav.prototype.onOpen;
/**
 * Event emitted when the sidenav is being closed. Use this to synchronize animations.
 * @type {?}
 */
MdSidenav.prototype.onCloseStart;
/**
 * Event emitted when the sidenav is fully closed.
 * @type {?}
 */
MdSidenav.prototype.onClose;
/**
 * Event emitted when the sidenav alignment changes.
 * @type {?}
 */
MdSidenav.prototype.onAlignChanged;
/**
 * The current toggle animation promise. `null` if no animation is in progress.
 * @type {?}
 */
MdSidenav.prototype._toggleAnimationPromise;
/**
 * The current toggle animation promise resolution function.
 * `null` if no animation is in progress.
 * @type {?}
 */
MdSidenav.prototype._resolveToggleAnimationPromise;
/** @type {?} */
MdSidenav.prototype._elementFocusedBeforeSidenavWasOpened;
/** @type {?} */
MdSidenav.prototype._elementRef;
/** @type {?} */
MdSidenav.prototype._focusTrapFactory;
/** @type {?} */
MdSidenav.prototype._doc;
}

/**
 * <md-sidenav-container> component.
 * 
 * This is the parent component to one or two <md-sidenav>s that validates the state internally
 * and coordinates the backdrop and content styling.
 */
export class MdSidenavContainer implements AfterContentInit {
   _sidenavs: QueryList<MdSidenav>;
/**
 * The sidenav child with the `start` alignment.
 * @return {?}
 */
get start() { return this._start; }
/**
 * The sidenav child with the `end` alignment.
 * @return {?}
 */
get end() { return this._end; }
/**
 * Event emitted when the sidenav backdrop is clicked.
 */
backdropClick = new EventEmitter<void>();
/**
 * The sidenav at the start/end alignment, independent of direction.
 */
private _start: MdSidenav;
private _end: MdSidenav;
/**
 * The sidenav at the left/right. When direction changes, these will change as well.
 * They're used as aliases for the above to set the left/right style properly.
 * In LTR, _left == _start and _right == _end.
 * In RTL, _left == _end and _right == _start.
 */
private _left: MdSidenav;
private _right: MdSidenav;
/**
 * Whether to enable open/close trantions.
 */
_enableTransitions = false;
/**
 * @param {?} _dir
 * @param {?} _element
 * @param {?} _renderer
 * @param {?} _ngZone
 */
constructor(
private _dir: Dir,
private _element: ElementRef,
private _renderer: Renderer2,
private _ngZone: NgZone) {
    // If a `Dir` directive exists up the tree, listen direction changes and update the left/right
    // properties to point to the proper start/end.
    if (_dir != null) {
      _dir.dirChange.subscribe(() => this._validateDrawers());
    }
  }
/**
 * @return {?}
 */
ngAfterContentInit() {
    // On changes, assert on consistency.
    this._sidenavs.changes.subscribe(() => this._validateDrawers());
    this._sidenavs.forEach((sidenav: MdSidenav) => {
      this._watchSidenavToggle(sidenav);
      this._watchSidenavAlign(sidenav);
    });
    this._validateDrawers();

    // Give the view a chance to render the initial state, then enable transitions.
    this._ngZone.onMicrotaskEmpty.first().subscribe(() => this._enableTransitions = true);
  }
/**
 * Calls `open` of both start and end sidenavs
 * @return {?}
 */
public open() {
    return Promise.all([this._start, this._end].map(sidenav => sidenav && sidenav.open()));
  }
/**
 * Calls `close` of both start and end sidenavs
 * @return {?}
 */
public close() {
    return Promise.all([this._start, this._end].map(sidenav => sidenav && sidenav.close()));
  }
/**
 * Subscribes to sidenav events in order to set a class on the main container element when the
 * sidenav is open and the backdrop is visible. This ensures any overflow on the container element
 * is properly hidden.
 * @param {?} sidenav
 * @return {?}
 */
private _watchSidenavToggle(sidenav: MdSidenav): void {
    if (!sidenav || sidenav.mode === 'side') { return; }
    sidenav.onOpen.subscribe(() => this._setContainerClass(true));
    sidenav.onClose.subscribe(() => this._setContainerClass(false));
  }
/**
 * Subscribes to sidenav onAlignChanged event in order to re-validate drawers when the align
 * changes.
 * @param {?} sidenav
 * @return {?}
 */
private _watchSidenavAlign(sidenav: MdSidenav): void {
    if (!sidenav) {
      return;
    }
    // NOTE: We need to wait for the microtask queue to be empty before validating,
    // since both drawers may be swapping sides at the same time.
    sidenav.onAlignChanged.subscribe(() =>
        this._ngZone.onMicrotaskEmpty.first().subscribe(() => this._validateDrawers()));
  }
/**
 * Toggles the 'mat-sidenav-opened' class on the main 'md-sidenav-container' element.
 * @param {?} isAdd
 * @return {?}
 */
private _setContainerClass(isAdd: boolean): void {
    if (isAdd) {
      this._renderer.addClass(this._element.nativeElement, 'mat-sidenav-opened');
    } else {
      this._renderer.removeClass(this._element.nativeElement, 'mat-sidenav-opened');
    }
  }
/**
 * Validate the state of the sidenav children components.
 * @return {?}
 */
private _validateDrawers() {
    this._start = this._end = null;

    // Ensure that we have at most one start and one end sidenav.
    // NOTE: We must call toArray on _sidenavs even though it's iterable
    // (see https://github.com/Microsoft/TypeScript/issues/3164).
    for (let /** @type {?} */ sidenav of this._sidenavs.toArray()) {
      if (sidenav.align == 'end') {
        if (this._end != null) {
          throwMdDuplicatedSidenavError('end');
        }
        this._end = sidenav;
      } else {
        if (this._start != null) {
          throwMdDuplicatedSidenavError('start');
        }
        this._start = sidenav;
      }
    }

    this._right = this._left = null;

    // Detect if we're LTR or RTL.
    if (this._dir == null || this._dir.value == 'ltr') {
      this._left = this._start;
      this._right = this._end;
    } else {
      this._left = this._end;
      this._right = this._start;
    }
  }
/**
 * @return {?}
 */
_onBackdropClicked() {
    this.backdropClick.emit();
    this._closeModalSidenav();
  }
/**
 * @return {?}
 */
_closeModalSidenav() {
    // Close all open sidenav's where closing is not disabled and the mode is not `side`.
    [this._start, this._end]
      .filter(sidenav => sidenav && !sidenav.disableClose && sidenav.mode !== 'side')
      .forEach(sidenav => sidenav.close());
  }
/**
 * @return {?}
 */
_isShowingBackdrop(): boolean {
    return (this._isSidenavOpen(this._start) && this._start.mode != 'side')
        || (this._isSidenavOpen(this._end) && this._end.mode != 'side');
  }
/**
 * @param {?} side
 * @return {?}
 */
private _isSidenavOpen(side: MdSidenav): boolean {
    return side != null && side.opened;
  }
/**
 * Return the width of the sidenav, if it's in the proper mode and opened.
 * This may relayout the view, so do not call this often.
 * @param {?} sidenav
 * @param {?} mode
 * @return {?}
 */
private _getSidenavEffectiveWidth(sidenav: MdSidenav, mode: string): number {
    return (this._isSidenavOpen(sidenav) && sidenav.mode == mode) ? sidenav._width : 0;
  }
/**
 * @return {?}
 */
_getMarginLeft() {
    return this._getSidenavEffectiveWidth(this._left, 'side');
  }
/**
 * @return {?}
 */
_getMarginRight() {
    return this._getSidenavEffectiveWidth(this._right, 'side');
  }
/**
 * @return {?}
 */
_getPositionLeft() {
    return this._getSidenavEffectiveWidth(this._left, 'push');
  }
/**
 * @return {?}
 */
_getPositionRight() {
    return this._getSidenavEffectiveWidth(this._right, 'push');
  }
/**
 * Returns the horizontal offset for the content area.  There should never be a value for both
 * left and right, so by subtracting the right value from the left value, we should always get
 * the appropriate offset.
 * @return {?}
 */
_getPositionOffset() {
    return this._getPositionLeft() - this._getPositionRight();
  }
/**
 * This is using [ngStyle] rather than separate [style...] properties because [style.transform]
 * doesn't seem to work right now.
 * @return {?}
 */
_getStyles() {
    return {
      marginLeft: `${this._getMarginLeft()}px`,
      marginRight: `${this._getMarginRight()}px`,
      transform: `translate3d(${this._getPositionOffset()}px, 0, 0)`
    };
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-sidenav-container, mat-sidenav-container',
  // Do not use ChangeDetectionStrategy.OnPush. It does not work for this component because
  // technically it is a sibling of MdSidenav (on the content tree) and isn't updated when MdSidenav
  // changes its state.
  templateUrl: 'sidenav-container.html',
  styleUrls: [
    'sidenav.css',
    'sidenav-transitions.css',
  ],
  host: {
    'class': 'mat-sidenav-container',
    '[class.mat-sidenav-transition]': '_enableTransitions',
  },
  encapsulation: ViewEncapsulation.None,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Dir, decorators: [{ type: Optional }, ]},
{type: ElementRef, },
{type: Renderer2, },
{type: NgZone, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_sidenavs': [{ type: ContentChildren, args: [MdSidenav, ] },],
'backdropClick': [{ type: Output },],
};
}

function MdSidenavContainer_tsickle_Closure_declarations() {
/** @type {?} */
MdSidenavContainer.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdSidenavContainer.ctorParameters;
/** @type {?} */
MdSidenavContainer.propDecorators;
/** @type {?} */
MdSidenavContainer.prototype._sidenavs;
/**
 * Event emitted when the sidenav backdrop is clicked.
 * @type {?}
 */
MdSidenavContainer.prototype.backdropClick;
/**
 * The sidenav at the start/end alignment, independent of direction.
 * @type {?}
 */
MdSidenavContainer.prototype._start;
/** @type {?} */
MdSidenavContainer.prototype._end;
/**
 * The sidenav at the left/right. When direction changes, these will change as well.
 * They're used as aliases for the above to set the left/right style properly.
 * In LTR, _left == _start and _right == _end.
 * In RTL, _left == _end and _right == _start.
 * @type {?}
 */
MdSidenavContainer.prototype._left;
/** @type {?} */
MdSidenavContainer.prototype._right;
/**
 * Whether to enable open/close trantions.
 * @type {?}
 */
MdSidenavContainer.prototype._enableTransitions;
/** @type {?} */
MdSidenavContainer.prototype._dir;
/** @type {?} */
MdSidenavContainer.prototype._element;
/** @type {?} */
MdSidenavContainer.prototype._renderer;
/** @type {?} */
MdSidenavContainer.prototype._ngZone;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
